export interface FlatDropAddress {
  streetAddress1: string;
  streetAddress2?: string;
  city: string;
  state: string;
  pincode?: string;
  country?: string;
  lat?: number;
  lng?: number;
  contactDetails?: {
    name: string;
    phoneNumber?: string;
  };
}

export interface DeliveryStopEntry {
  address: FlatDropAddress | { address?: FlatDropAddress };
  status: "PENDING" | "DELIVERED";
  deliveredAt?: string;
}

export type DropDetails =
  | {
      address?: FlatDropAddress;
      [orderNo: string]: unknown;
    }
  | undefined;

export interface ShipmentDeliveryStopsCarrier {
  customerIds?: unknown[];
  orderNumbers?: string[];
  dropDetails?: unknown;
  deliveryStops?: unknown;
}

const DROP_DETAILS_RESERVED_KEYS = new Set(["address"]);

export const unwrapDropAddress = (
  value: unknown,
): FlatDropAddress | undefined => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }

  const record = value as Record<string, unknown>;
  if (typeof record.streetAddress1 === "string") {
    return value as FlatDropAddress;
  }

  return unwrapDropAddress(record.address);
};

const asStopMap = (
  value: unknown,
): Record<string, DeliveryStopEntry> => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  const result: Record<string, DeliveryStopEntry> = {};
  for (const [key, rawStop] of Object.entries(value as Record<string, unknown>)) {
    if (DROP_DETAILS_RESERVED_KEYS.has(key) || !rawStop || typeof rawStop !== "object") {
      continue;
    }

    const stop = rawStop as Record<string, unknown>;
    const address = unwrapDropAddress(stop.address ?? stop);
    if (!address) {
      continue;
    }

    result[key] = {
      address,
      status: stop.status === "DELIVERED" ? "DELIVERED" : "PENDING",
      deliveredAt:
        typeof stop.deliveredAt === "string" ? stop.deliveredAt : undefined,
    };
  }

  return result;
};

export const getOrderDropStops = (
  shipment: ShipmentDeliveryStopsCarrier,
): Record<string, DeliveryStopEntry> => ({
  ...asStopMap(shipment.deliveryStops),
  ...asStopMap(shipment.dropDetails),
});

export const isMultiCustomerTrip = (
  shipment: ShipmentDeliveryStopsCarrier,
): boolean => (shipment.customerIds?.length ?? 0) > 1;

export const isMultiStopTrip = (
  shipment: ShipmentDeliveryStopsCarrier,
): boolean =>
  isMultiCustomerTrip(shipment) ||
  Object.keys(getOrderDropStops(shipment)).length > 1;

export const getDeliveryStopStatus = (
  shipment: ShipmentDeliveryStopsCarrier,
  orderNo: string,
): "PENDING" | "DELIVERED" | undefined =>
  getOrderDropStops(shipment)[orderNo]?.status;

export const getPrimaryDropAddress = (
  shipment: ShipmentDeliveryStopsCarrier,
): FlatDropAddress | undefined => {
  const dropDetails =
    shipment.dropDetails &&
    typeof shipment.dropDetails === "object" &&
    !Array.isArray(shipment.dropDetails)
      ? (shipment.dropDetails as { address?: unknown })
      : undefined;
  const fromRoot = unwrapDropAddress(dropDetails?.address);
  if (fromRoot) {
    return fromRoot;
  }

  const firstStop = Object.values(getOrderDropStops(shipment))[0];
  return unwrapDropAddress(firstStop?.address);
};

export const formatMultiStopDestinationAddress = (
  shipment: ShipmentDeliveryStopsCarrier,
  fallbackAddress: string,
): string => {
  const stops = getOrderDropStops(shipment);

  if (!stops || Object.keys(stops).length === 0) {
    return fallbackAddress;
  }

  return Object.entries(stops)
    .map(([orderNo, stop]) => {
      const address = unwrapDropAddress(stop.address);
      return `${orderNo}: ${address?.streetAddress1 ?? ""}, ${address?.city ?? ""}`;
    })
    .join(" | ");
};

export const getMultiCustomerLabel = (
  customerIds: string[] | undefined,
): string => `${customerIds?.length ?? 0} Customers`;
